// HelpTip
// -------
// The Layer-2 primitive of the contextual-help system: a small info icon
// that reveals a short definition or explanation on hover / focus / tap.
//
// Encapsulates both halves of the react-tooltip pattern (the trigger and
// its floating tooltip) behind one self-contained element with a unique
// id, so call sites drop in a single <HelpTip content="…" /> next to a
// label instead of hand-managing a data-tooltip-id + a separate <Tooltip>
// placed elsewhere in the tree.
//
// Use for compact term definitions (a test status, a role, a field format).
// For longer, multi-example explanations prefer <Explainer>, the Layer-3
// collapsible.

import React, { useId } from "react";
import { FiInfo } from "react-icons/fi";
import { Tooltip } from "react-tooltip";
import { cn } from "@/lib/utils";

export interface HelpTipProps {
  // Tooltip body. Accepts rich content (bold, code, lists, links); keep it
  // to a sentence or two — reach for <Explainer> when it grows past that.
  content: React.ReactNode;
  // Accessible name for the trigger, announced by screen readers and shown
  // as the native title. Describe what the tip explains, e.g. "About test
  // statuses", not "info".
  label: string;
  // Preferred side to open on; react-tooltip flips automatically when there
  // isn't room. Defaults to "top".
  place?: "top" | "bottom" | "left" | "right";
  // Extra classes on the trigger (sizing, color, margin at the call site).
  className?: string;
  // Max tooltip width in px. Defaults to 260 — wide enough for a sentence,
  // narrow enough to stay readable.
  maxWidthPx?: number;
}

export default function HelpTip({
  content,
  label,
  place = "top",
  className,
  maxWidthPx = 260,
}: HelpTipProps) {
  // useId gives a stable, collision-free id per instance (SSR-safe, and
  // unaffected by the Math.random / Date.now restrictions elsewhere).
  const tooltipId = `helptip-${useId()}`;

  return (
    <>
      <button
        type="button"
        data-tooltip-id={tooltipId}
        aria-label={label}
        title={label}
        // A tip is supplementary, never a form control — keep it out of the
        // tab-submit path but still keyboard-reachable and focus-visible.
        className={cn(
          "inline-flex items-center justify-center align-middle text-gray-400 " +
            "transition-colors hover:text-gray-600 focus:outline-none " +
            "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded-full",
          className,
        )}
      >
        <FiInfo className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <Tooltip
        id={tooltipId}
        place={place}
        // clickable so links / selectable text inside the tip work; it stays
        // hover/focus-triggered (not openOnClick) so it behaves like a tooltip.
        clickable
        opacity={1}
        style={{ maxWidth: maxWidthPx, zIndex: 70 }}
        className="!rounded-md !bg-gray-900 !px-3 !py-2 !text-xs !font-normal !leading-relaxed !text-white shadow-lg"
      >
        {content}
      </Tooltip>
    </>
  );
}
